import type { CatalogColor } from '~~/lib/colorCatalog'
import { CACHE_TAG_CONTEXT_KEY } from '~~/lib/htmlCacheTags'

/** Public, channel-scoped metadata, shared by every swatch on this Nuxt request. */
export function useColorCatalog() {
  const nuxtApp = useNuxtApp()
  if (import.meta.server) {
    // A fallback page rendered while the API is unavailable still depends on
    // the catalog. The first import/edit must invalidate that cached HTML too.
    const event = useRequestEvent()
    if (event) {
      const tags: Set<string> = event.context[CACHE_TAG_CONTEXT_KEY] ??= new Set()
      tags.add('op:colorCatalog')
    }
  }
  const key = computed(() => `colorCatalog:${nuxtApp.$i18n.locale.value}`)
  return useAsyncData(
    key,
    async (): Promise<CatalogColor[]> => {
      // Never retain another market's data when a locale switch fails.
      const previous = nuxtApp.payload.data[key.value] as CatalogColor[] | undefined
      try {
        const result = await useGraphqlQuery('colorCatalog', null, { fetchOptions: { timeout: 3000, retry: 0 } })
        if (result.errors?.length || !Array.isArray(result.data?.colorCatalog))
          return previous ?? []
        // Navigation revalidation must not refetch the current product grid
        // merely because an unchanged catalog arrived as a new array object.
        return previous && JSON.stringify(previous) === JSON.stringify(result.data.colorCatalog)
          ? previous
          : result.data.colorCatalog
      }
      catch {
        // During the backend-first rollout an older API can lack this field.
        // Keep the last successful catalog, or the unchanged legacy fallback.
        return previous ?? []
      }
    },
    {
      default: () => [] as CatalogColor[],
      dedupe: 'defer',
      getCachedData: (key, nuxtApp, ctx) =>
        ctx.cause === 'refresh:manual' || ctx.cause === 'refresh:hook'
          ? undefined
          : nuxtApp.payload.data[key] as CatalogColor[] | undefined,
    },
  )
}
